/**
 * The one-line caption under each of `#uc2Card`'s three charts (client §3.1: "each with a
 * one-line caption", every caption ≤ 8 words).
 *
 * Computed in the pane, not served: the caption is a reading of the same numbers the chart
 * draws, and deriving it here is what keeps the two in step. Each function returns a
 * **locale key plus its interpolation**, never a sentence, so the words themselves stay in
 * `locales/{en,fr}.json` where the vocabulary test can sweep them.
 *
 * The band is the engine's own `stability_band`, served on `uc2/summary` and passed in by
 * the pane, so a caption can never say "stable" about a watershed the map painted as
 * growing — nor keep saying "±5 %" after a curator has moved the config. The default is
 * only for a summary (a pre-launch run, or a request still in flight) that carries none.
 */
import { fmtFixed } from '../../lib/format';
import { STABILITY_BAND_PCT, UC2_ADOPTION_GAP_POINTS } from './uc2Constants';

export interface Caption {
  key: string;
  options?: Record<string, unknown>;
}

/** "Cropland is stable" — from the same relative change the cropland chip paints. */
export function croplandCaption(
  delta: number | null | undefined,
  band: number = STABILITY_BAND_PCT,
): Caption | null {
  if (typeof delta !== 'number' || !Number.isFinite(delta)) return null;
  if (delta < -band) return { key: 'intel.uc2.caption.cropland.declining' };
  if (delta > band) return { key: 'intel.uc2.caption.cropland.growing' };
  return { key: 'intel.uc2.caption.cropland.stable' };
}

/**
 * "2025 near average" — the most recent year of the rainfall series against the 2017–2025
 * mean, on the same band. `null` when either number is missing, in which case the
 * chart simply carries no caption rather than an invented one.
 */
export function rainCaption(
  latest: { year: number; mm: number | null | undefined } | null,
  mean: number | null | undefined,
  band: number = STABILITY_BAND_PCT,
): Caption | null {
  if (!latest || typeof latest.mm !== 'number' || typeof mean !== 'number' || !mean) return null;
  const ratio = latest.mm / mean;
  const fraction = band / 100;
  const options = { year: latest.year };
  if (ratio > 1 + fraction) return { key: 'intel.uc2.caption.rain.above', options };
  if (ratio < 1 - fraction) return { key: 'intel.uc2.caption.rain.below', options };
  return { key: 'intel.uc2.caption.rain.near', options };
}

/**
 * "Agroforestry above average" — this watershed's adoption for the selected group against
 * the programme mean for the same group, on the same band.
 */
export function practiceCaption(
  practiceLabel: string,
  value: number | null | undefined,
  mean: number | null | undefined,
  band: number = STABILITY_BAND_PCT,
): Caption | null {
  if (typeof value !== 'number' || typeof mean !== 'number') return null;
  const options = { practice: practiceLabel };
  if (value > mean + band) return { key: 'intel.uc2.caption.practice.above', options };
  if (value < mean - band) return { key: 'intel.uc2.caption.practice.below', options };
  return { key: 'intel.uc2.caption.practice.near', options };
}

/* ------------------------------------------------------------------------------------ *
 * The 2 Oct tab-analytics round (design `2026-10-02-uc2-tabs-analytics-design.md`): the
 * captions of the three tabs' programme-wide blocks. Same contract as the three above —
 * a locale key plus its interpolation, computed from the very numbers the chart draws, so
 * the words and the picture can never drift apart — and the same `null` when the block
 * carries nothing to read.
 * ------------------------------------------------------------------------------------ */

/** A year point of a class series, as the captions read it. */
export interface CaptionPoint {
  value: number | null | undefined;
}

/** The series' change from its first measured year to its last, in percent of the first. */
export function seriesChangePct(points: CaptionPoint[] | null | undefined): number | null {
  const values = (points ?? [])
    .map((p) => p.value)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (values.length < 2) return null;
  const first = values[0]!;
  const last = values[values.length - 1]!;
  if (!first) return null;
  return ((last - first) / Math.abs(first)) * 100;
}

/** The series' own average over its measured years. */
export function seriesMean(points: CaptionPoint[] | null | undefined): number | null {
  const values = (points ?? [])
    .map((p) => p.value)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * How many watersheds a class caption is speaking for — `by_class_series[scope].n.positive`.
 * A caption that knows it says so ("7 positive areas: cropland rising"), because "positive
 * areas" over three watersheds and over thirty are not the same claim; a run that serves no
 * count falls back to the `…Any` sentence rather than printing a bare `{{count}}`.
 */
function counted(key: string, n: number | null | undefined, options: Record<string, unknown> = {}) {
  // `count` is i18next's own plural selector (`…rising_one` / `…rising_other`), so one
  // positive area never reads as "1 positive areas".
  return typeof n === 'number' && Number.isFinite(n)
    ? { key, options: { ...options, count: n } }
    : { key: `${key}Any`, options };
}

/**
 * "7 positive areas: cropland rising" — the direction of the positive class's own cropland
 * series over the period, on the engine's stability band.
 */
export function classTrendCaption(
  positive: CaptionPoint[] | null | undefined,
  band: number = STABILITY_BAND_PCT,
  n?: number | null,
): Caption | null {
  const change = seriesChangePct(positive);
  if (change == null) return null;
  if (change > band) return counted('intel.uc2.caption.trend.cropland.rising', n);
  if (change < -band) return counted('intel.uc2.caption.trend.cropland.falling', n);
  return counted('intel.uc2.caption.trend.cropland.steady', n);
}

/**
 * "Rainfall alike in both groups" — the two classes' average rainfall against each other,
 * on the same band. This is the caption that keeps a reader from taking a wetter run of
 * years for the thing the positive class is about.
 */
export function classRainCaption(
  positive: CaptionPoint[] | null | undefined,
  rest: CaptionPoint[] | null | undefined,
  band: number = STABILITY_BAND_PCT,
  n?: number | null,
): Caption | null {
  const a = seriesMean(positive);
  const b = seriesMean(rest);
  if (a == null || b == null || !b) return null;
  const relative = ((a - b) / Math.abs(b)) * 100;
  if (relative > band) return counted('intel.uc2.caption.trend.rain.wetter', n);
  if (relative < -band) return counted('intel.uc2.caption.trend.rain.drier', n);
  // "Alike" is about both groups at once, so neither count belongs in it.
  return { key: 'intel.uc2.caption.trend.rain.alike' };
}

/** "Kou greening fastest" — the top row of the greening ranking, or the flat-period line. */
export function greeningCaption(
  rows: { name: string; ndvi_trend: number | null }[] | null | undefined,
): Caption | null {
  const measured = (rows ?? []).filter(
    (r) => typeof r.ndvi_trend === 'number' && Number.isFinite(r.ndvi_trend),
  );
  if (!measured.length) return null;
  const top = measured.reduce((best, r) => (r.ndvi_trend! > best.ndvi_trend! ? r : best));
  if (top.ndvi_trend! <= 0) return { key: 'intel.uc2.caption.trend.greening.flat' };
  return { key: 'intel.uc2.caption.trend.greening.top', options: { name: top.name } };
}

/**
 * "Agroforestry +18 points in 7 positive areas" — the practice group whose adoption
 * differs most between the two classes, named with its difference in points and the size
 * of the group it is measured over. A block where no group is higher in the positive class
 * says exactly that instead.
 */
export function practicesTopCaption(
  diff: Record<string, number | null | undefined> | null | undefined,
  labelOf: (group: string) => string,
  n?: number | null,
): Caption | null {
  const entries = Object.entries(diff ?? {}).filter(
    (e): e is [string, number] => typeof e[1] === 'number' && Number.isFinite(e[1]),
  );
  if (!entries.length) return null;
  const top = entries.reduce((best, e) => (e[1] > best[1] ? e : best));
  if (top[1] <= 0) return { key: 'intel.uc2.caption.practices.none' };
  /**
   * **One decimal, the same as everything else on this tab** (client round 2 Oct 2026).
   * The caption used to round to a whole number, so a 1.447-point difference was printed
   * as "+1 point" here and as "+1.4 points" in the row and the takeaway two lines below —
   * one number, three lines, two different facts as far as a reader is concerned.
   *
   * Two things are counted in this one sentence and i18next's `count` can only select for
   * one of them, so the points pick the key and the areas pick its plural suffix —
   * otherwise a difference of exactly one point reads as "+1.0 points".
   */
  const points = fmtFixed(top[1], 1);
  const stem =
    Math.abs(top[1] - 1) < 0.05
      ? 'intel.uc2.caption.practices.topPoint'
      : 'intel.uc2.caption.practices.top';
  return counted(stem, n, { practice: labelOf(top[0]), points });
}

/**
 * The plain notes under a Similar-tab card (client round 2 October 2026: "say what is
 * happening and what to look out for, in words a non-technical reader can act on").
 *
 * Like every other function here these return a **locale key plus its interpolation**, so
 * the sentences themselves stay in the bundles where the UC2 vocabulary sweep can see
 * them, and the branch below is the only thing this module decides.
 */

/**
 * Which of the four "what to look out for" sentences a pair gets.
 *
 * `gap` is `a − b` in points, the same signed number the card's own sentence reads, and it
 * is **rounded before the branch is chosen** for the reason the card already rounds first:
 * a 14.6-point gap that prints as "15 points" must not be described as a similar level.
 *
 * `learning` is the engine's own flag (`gap >= adoption_gap` on the stored pair), and it
 * wins whenever it is set — the threshold is the definition's, not this build's, so a
 * curator who moves it moves the wording with it. {@link UC2_ADOPTION_GAP_POINTS} is only
 * the fallback for the mirror-image case the engine stores no flag for.
 */
export function pairNoteBranch(
  gap: number | null | undefined,
  learning: boolean,
  threshold: number = UC2_ADOPTION_GAP_POINTS,
): 'learning' | 'similar' | 'reverse' | 'missing' {
  if (typeof gap !== 'number' || !Number.isFinite(gap)) return 'missing';
  const points = Math.round(gap);
  if (learning || points >= threshold) return 'learning';
  if (points <= -threshold) return 'reverse';
  return 'similar';
}

/** The "what to look out for" sentence for a pair, as a key and its interpolation. */
export function pairLookOut(
  branch: ReturnType<typeof pairNoteBranch>,
  names: { a: string; b: string },
  practice: string,
  gap: number | null | undefined,
  /** Which side the survey value is missing from, for the `missing` branch; `b` by default. */
  missing: 'a' | 'b' = 'b',
): Caption {
  const points = typeof gap === 'number' && Number.isFinite(gap) ? Math.abs(Math.round(gap)) : 0;
  const key = `intel.uc2.note.pair${branch[0]!.toUpperCase()}${branch.slice(1)}`;
  if (branch === 'missing') {
    return { key, options: { a: names.a, b: names[missing], practice, gap: points, count: points } };
  }
  // `count` only picks the plural suffix; `gap` is what prints, so the two can never
  // disagree about a one-point difference ("1 points" is the bug this spells away).
  return { key, options: { a: names.a, b: names.b, practice, gap: points, count: points } };
}

/* ------------------------------------------------------------------------------------ *
 * The scaling round (3 October 2026): the one-sentence reading under `#uc2Drivers` while
 * a unit is focused. Same contract as everything above — locale keys and their
 * interpolation, never a sentence — so the words stay where the vocabulary sweep can see
 * them and the branch below is all this module decides.
 * ------------------------------------------------------------------------------------ */

/** Which verb a signed change takes, on the run's own band: grew / held / shrank. */
export function changeVerb(
  change: number | null | undefined,
  band: number = STABILITY_BAND_PCT,
): 'grew' | 'held' | 'shrank' | null {
  if (typeof change !== 'number' || !Number.isFinite(change)) return null;
  if (change > band) return 'grew';
  if (change < -band) return 'shrank';
  return 'held';
}

/** Which word a signed rainfall change takes, on the same band. */
export function rainWord(
  change: number | null | undefined,
  band: number = STABILITY_BAND_PCT,
): 'wetter' | 'near' | 'drier' | null {
  if (typeof change !== 'number' || !Number.isFinite(change)) return null;
  if (change > band) return 'wetter';
  if (change < -band) return 'drier';
  return 'near';
}

/**
 * One land clause.
 *
 * **"Held" prints a level, the other two print a change**, which is the one asymmetry
 * worth spelling out: "held at 97 % of its 2017 level" says the thing stayed put far
 * better than "held 3 %" ever could, while "grew 64 %" is already a plain reading of the
 * change itself. The number is rounded before the branch is chosen, so a change that
 * prints as the band's own edge is never described on the other side of it.
 */
function landClause(
  field: 'farmland' | 'veg',
  change: number | null | undefined,
  band: number,
): Caption | null {
  const verb = changeVerb(change, band);
  if (!verb) return null;
  const value = change as number;
  const pct = verb === 'held' ? Math.round(100 + value) : Math.abs(Math.round(value));
  return { key: `intel.uc2.drivers.${field}.${verb}`, options: { pct } };
}

/**
 * One country's clause: not "farmland grew 4 %", which is a mean of eleven places and
 * says nothing about how many of them are behind it, but **in how many of them the check
 * passed**. The count is printed when the caller knows how many watersheds the country
 * has; the share alone when it does not.
 */
function countryClause(
  field: 'farmland' | 'veg' | 'greening',
  sharePct: number | null | undefined,
  total: number | null | undefined,
): Caption | null {
  if (typeof sharePct !== 'number' || !Number.isFinite(sharePct)) return null;
  const pct = Math.round(Math.min(100, Math.max(0, sharePct)));
  if (typeof total === 'number' && Number.isFinite(total) && total > 0) {
    const n = Math.round((pct / 100) * total);
    /**
     * The plural is selected on **`total`**, not on `n`: the noun the suffix agrees with
     * is "of {{total}} watersheds" in both bundles, and selecting on `n` printed
     * "1 of 3 watershed".
     */
    return {
      key: `intel.uc2.drivers.country.${field}`,
      options: { n, total, count: total },
    };
  }
  return { key: `intel.uc2.drivers.country.${field}Pct`, options: { pct } };
}

/** The greening leg, once the run serves a verdict on it; `null` until it does. */
function greeningClause(ok: boolean | null | undefined): Caption | null {
  if (ok == null) return null;
  return { key: `intel.uc2.drivers.greening.${ok ? 'up' : 'notUp'}` };
}

/** What {@link driversReading} reads, which is one row of either level. */
export interface DriversInput {
  cropland_change_pct?: number | null;
  veg_change_pct?: number | null;
  rain_change_pct?: number | null;
  greening_ok?: boolean | null;
  share_cropland_ok_pct?: number | null;
  share_veg_ok_pct?: number | null;
  share_greening_ok_pct?: number | null;
}

/**
 * The focused unit's reading, as its pieces: the land clauses in order, the greening
 * clause once the run has one (`greening_ok` at watershed level, the share at country
 * level), and the rainfall clause. The caller joins them, because
 * the joiner is itself a locale string ("{{a}} and {{b}}") and this module deliberately
 * holds no translator.
 *
 * At **country level** the two land clauses count watersheds rather than describing a
 * mean: `country` turns that branch on, and `total` is how many watersheds the country
 * has (the count is dropped for a caller that does not know).
 *
 * `null` when there is nothing at all to read, so the block prints no sentence rather
 * than an empty one.
 */
export interface DriversReading {
  land: Caption[];
  rain: Caption | null;
}

export function driversReading(
  drivers: DriversInput | null | undefined,
  band: number = STABILITY_BAND_PCT,
  country: { total?: number | null } | null = null,
): DriversReading | null {
  if (!drivers) return null;
  const land = (
    country
      ? [
          countryClause('farmland', drivers.share_cropland_ok_pct, country.total),
          countryClause('veg', drivers.share_veg_ok_pct, country.total),
          // A country has no `greening_ok` of its own: the share is its answer, read the
          // same way as the other two.
          countryClause('greening', drivers.share_greening_ok_pct, country.total),
        ]
      : [
          landClause('farmland', drivers.cropland_change_pct, band),
          landClause('veg', drivers.veg_change_pct, band),
          greeningClause(drivers.greening_ok),
        ]
  ).filter((c): c is Caption => c != null);
  const word = rainWord(drivers.rain_change_pct, band);
  const rain = word ? { key: `intel.uc2.drivers.rain.${word}` } : null;
  if (!land.length && !rain) return null;
  return { land, rain };
}
